import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Alert, Button, Spin } from "antd";

import settings from "~/settings";
import { getAuthCaptchaCallbackResult } from "~/utils/authCaptcha.cjs";

const AuthCaptcha = forwardRef(
  (
    {
      onVerified,
      onError,
      onRetry,
      onStatusChange,
      disabled = false,
      visibleTrigger = false,
      autoStart = false,
    },
    ref
  ) => {
    const reactId = useId().replace(/:/g, "");
    const elementId = `auth-captcha-element-${reactId}`;
    const buttonId = `auth-captcha-button-${reactId}`;
    const [status, setStatus] = useState("loading");
    const [error, setError] = useState(null);
    const [initGeneration, setInitGeneration] = useState(0);
    const initializedRef = useRef(false);
    const verifyingRef = useRef(false);
    const instanceRef = useRef(null);
    const pendingStartRef = useRef(false);
    const autoStartedRef = useRef(false);
    const startLockRef = useRef(false);
    const reportedCaptchaErrorRef = useRef(false);
    const onVerifiedRef = useRef(onVerified);
    const onErrorRef = useRef(onError);
    const onStatusChangeRef = useRef(onStatusChange);
    const disabledRef = useRef(disabled);

    onVerifiedRef.current = onVerified;
    onErrorRef.current = onError;
    onStatusChangeRef.current = onStatusChange;
    disabledRef.current = disabled;

    const releaseCaptcha = useCallback(() => {
      verifyingRef.current = false;
      pendingStartRef.current = false;
      startLockRef.current = false;
      autoStartedRef.current = false;
    }, []);

    const resetToReady = useCallback(() => {
      reportedCaptchaErrorRef.current = false;
      releaseCaptcha();
      setError(null);
      setStatus("ready");
    }, [releaseCaptcha]);

    const reportError = useCallback((message) => {
      reportedCaptchaErrorRef.current = true;
      setError(message);
      setStatus("error");
      releaseCaptcha();
      onErrorRef.current?.(message);
    }, [releaseCaptcha]);

    const start = useCallback(() => {
      if (disabledRef.current || startLockRef.current || verifyingRef.current) {
        return;
      }

      const instance = instanceRef.current;
      if (instance && typeof instance.startTracelessVerification === "function") {
        startLockRef.current = true;
        setError(null);
        setStatus("verifying");
        instance.startTracelessVerification();
        return;
      }

      pendingStartRef.current = true;
    }, []);

    useImperativeHandle(ref, () => ({ start }), [start]);

    useEffect(() => {
      onStatusChangeRef.current?.(status);
    }, [status]);

    useEffect(() => {
      if (!autoStart) {
        autoStartedRef.current = false;
        return undefined;
      }

      if (status === "ready" && !autoStartedRef.current && !verifyingRef.current) {
        autoStartedRef.current = true;
        start();
      }

      return undefined;
    }, [autoStart, start, status]);

    useEffect(() => {
      let cancelled = false;
      initializedRef.current = false;
      const sceneId = settings.authAliyunCaptchaSceneId;

      if (!sceneId) {
        reportError("人机验证未配置，请稍后再试");
        return undefined;
      }

      const captchaVerifyCallback = async (captchaVerifyParam, callback) => {
        const complete = (captchaResult, bizResult) => {
          const result = { captchaResult, bizResult };
          if (typeof callback === "function") {
            callback(result);
          }
          return result;
        };

        if (verifyingRef.current) {
          return complete(false, false);
        }

        if (!captchaVerifyParam) {
          reportError("人机验证失败，请重试");
          return complete(false, false);
        }

        verifyingRef.current = true;
        setError(null);
        setStatus("verifying");

        try {
          await onVerifiedRef.current(captchaVerifyParam);
          verifyingRef.current = false;
          autoStartedRef.current = false;
          startLockRef.current = false;
          instanceRef.current?.destroyCaptcha?.();
          instanceRef.current = null;
          initializedRef.current = false;
          setInitGeneration((generation) => generation + 1);
          return complete(true, true);
        } catch (err) {
          const { captchaResult, bizResult } = getAuthCaptchaCallbackResult(err);
          if (captchaResult === false) {
            reportError(err?.message || "人机验证失败，请重试");
            return complete(captchaResult, bizResult);
          }

          resetToReady();
          return complete(captchaResult, bizResult);
        }
      };

      const onBizResultCallback = (bizResult) => {
        if (bizResult === false && !reportedCaptchaErrorRef.current) {
          resetToReady();
        }
      };

      const beginPendingStart = (instance) => {
        if (!pendingStartRef.current || cancelled) {
          return;
        }
        pendingStartRef.current = false;
        startLockRef.current = true;
        setStatus("verifying");
        instance.startTracelessVerification();
      };

      const initialize = () => {
        if (
          cancelled ||
          initializedRef.current ||
          typeof window === "undefined" ||
          typeof window.initAliyunCaptcha !== "function"
        ) {
          return;
        }

        initializedRef.current = true;
        window.initAliyunCaptcha({
          SceneId: sceneId,
          mode: "popup",
          element: `#${elementId}`,
          button: `#${buttonId}`,
          captchaVerifyCallback,
          onBizResultCallback,
          getInstance: (instance) => {
            instanceRef.current = instance;
            if (cancelled) {
              return;
            }
            setStatus("ready");
            beginPendingStart(instance);
          },
          slideStyle: {
            width: 360,
            height: 40,
          },
          language: "cn",
        });
      };

      setStatus("loading");
      initialize();
      const pollTimer = window.setInterval(initialize, 200);
      const timeoutTimer = window.setTimeout(() => {
        if (!initializedRef.current && !cancelled) {
          reportError("验证组件加载失败，请刷新页面重试");
        }
      }, 10000);

      return () => {
        cancelled = true;
        window.clearInterval(pollTimer);
        window.clearTimeout(timeoutTimer);
        instanceRef.current?.destroyCaptcha?.();
        instanceRef.current = null;
      };
    }, [buttonId, elementId, initGeneration, reportError, resetToReady]);

    return (
      <div
        aria-live="polite"
        aria-busy={status === "loading" || status === "verifying"}
      >
        <div id={elementId} />
        {visibleTrigger ? (
          <>
            {status === "loading" && !error && (
              <div className="text-center mb-10">
                <Spin size="small" /> <span>正在加载人机验证</span>
              </div>
            )}
            <Button
              id={buttonId}
              type="primary"
              block
              disabled={disabled || status !== "ready"}
              aria-describedby={error ? `${elementId}-error` : undefined}
            >
              {status === "verifying" ? "正在验证" : "开始验证"}
            </Button>
          </>
        ) : (
          <button type="button" id={buttonId} hidden tabIndex={-1} aria-hidden="true" />
        )}
        {error && (
          <div className="mt-10">
            <Alert
              id={`${elementId}-error`}
              type="error"
              showIcon
              message={error}
            />
            {onRetry && (
              <Button className="mt-10" block onClick={onRetry} disabled={disabled}>
                重新加载验证
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }
);

AuthCaptcha.displayName = "AuthCaptcha";

export default AuthCaptcha;
